import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Carousel,
  CarouselItem,
  CustomBackdrop,
  Header,
  NavHeader,
  Page,
  PopUpNav,
  Tabs,
} from '../../../../components';
import { IconShare } from '../../../../assets/popup-nav-icons';
import { ChannelTypeInternal, UserPermissionType } from '../../../../data-access/gql-types/graphql';
import { useUserPermissions } from '../../../../hooks/user/use-user-permissions';
import { useShareRedirect } from '../../../../modules/installation-share/hooks/use-share-redirect';
import { ROUTES } from '../../../../routes/routes';
import '../../../../styles/common/channel-configuration.scss';
import { pathnameWithParameters } from '../../../../utils/location';
import { useDevicesAndChannelsContext } from '../../context/devices-and-channels';
import { useGetChannelDetails } from '../channel-details/hooks/use-get-channel-details';
import ChannelConfigurationForm from './channel-form';
import InfoPage from './components/info-page';
import ChannelGeneralConfiguration from './general-configuration';
import { InfoPageType } from './types';
import { configurationExists } from './utils';

const ChannelConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation('configuration');
  const { t: td } = useTranslation('channel-details');
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const { channelType, channelId } = useParams<{ channelType: ChannelTypeInternal; channelId: string }>();
  const ChannelId = channelId ?? '';
  const { permissions } = useUserPermissions();
  const { channel } = useDevicesAndChannelsContext();
  const { loading } = useGetChannelDetails(ChannelId);
  const [infoPage, setInfoPage] = useState<InfoPageType>(InfoPageType.UNDEFINED);
  const { shareChannel } = useShareRedirect();

  useEffect(() => {
    const tab = new URLSearchParams(search).get('tab');
    if (tab) setActiveTabIndex(parseInt(tab));
  }, [search]);

  const configurationVisible = useMemo(
    () =>
      channelType && configurationExists.includes(channelType) && permissions[UserPermissionType.ChannelConfiguration],
    [channelType, permissions],
  );

  const tabs = [{ label: t('general') }, ...(configurationVisible ? [{ label: t('configuration') }] : [])];

  const popupNavOptions = useMemo(
    () =>
      permissions[UserPermissionType.ChannelShare]
        ? [
            {
              onClick: () => shareChannel(channel),
              icon: <IconShare />,
              label: td('shareChannel'),
            },
          ]
        : [],
    [permissions[UserPermissionType.ChannelShare]],
  );

  const handleTabChange = (tabIndex: number) => {
    navigate(
      pathnameWithParameters(ROUTES.ChannelConfiguration(channelType, ChannelId), search, {
        key: 'tab',
        value: tabIndex,
      }),
      { replace: true },
    );
  };

  return (
    <>
      {infoPage === InfoPageType.UNDEFINED ? (
        <Page
          isStickyHeader
          containerClassName="page__container--flex-height"
          className="channel-configuration"
          header={
            <>
              <NavHeader>
                <PopUpNav links={popupNavOptions} />
              </NavHeader>
              <Header title={t('channelSettings')} />
              <Tabs
                tabList={tabs}
                activeTabIndex={activeTabIndex}
                setActiveTabIndex={handleTabChange}
                tabsType="pills"
                isDivider={false}
              />
            </>
          }
        >
          <Carousel setActiveIndex={handleTabChange} activeIndex={activeTabIndex}>
            <CarouselItem key={0}>
              <div className="channel-configuration--content max-width-desktop">
                <ChannelGeneralConfiguration setInfoPage={setInfoPage} />
              </div>
            </CarouselItem>
            <CarouselItem key={1}>
              <div className="channel-configuration--content max-width-desktop">
                <ChannelConfigurationForm setInfoPage={setInfoPage} />
              </div>
            </CarouselItem>
          </Carousel>
        </Page>
      ) : (
        <InfoPage infoPage={infoPage} changePage={setInfoPage} />
      )}
      <CustomBackdrop loading={loading} />
    </>
  );
};

export default ChannelConfiguration;
